import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  Typography,
  Avatar,
  CircularProgress,
  IconButton,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  VerifiedUser as VerifiedIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  ChatMessage,
  SentenceAnnotation,
  VerificationSummary,
  WebSearchStatus,
} from '../../types/chat';
import { useTranslation } from 'react-i18next';
import AionaIcon from '../../assets/aiona/AIONA_ファビコン.png';
import SearchResultsCard, { SearchResult } from './SearchResultsCard';
import MarkdownRenderer, { isMarkdownContent } from '../common/MarkdownRenderer';

// Extended message type with search results and grounding
export interface ExtendedChatMessage extends Omit<ChatMessage, 'searchResults'> {
  searchResults?: {
    query: string;
    results: SearchResult[];
  };
  groundingStatus?: string;
  verificationSummary?: VerificationSummary;
  webSearchStatus?: WebSearchStatus;
}

interface NotionStyleMessageListProps {
  messages: ExtendedChatMessage[];
  isLoading: boolean;
  onSearchResultClick?: (result: SearchResult, query: string) => void;
  onAddToContext?: (results: SearchResult[]) => Promise<void>;
  selectedFileIds?: string[];
  onRetryWithMode?: (message: string) => void;
}

// ------------------------------------------------------------------
// Verification badge + collapsible details (Sprint 10)
// ------------------------------------------------------------------

// WCAG 2.1 AA: filled chips with white text (contrast ≥ 4.5:1)
// Each level uses a distinct icon shape so color is not the only differentiator
const VERIFICATION_CONFIG: Record<string, { labelKey: string; color: 'success' | 'warning' | 'error'; icon: React.ReactNode }> = {
  ok: { labelKey: 'chat.verificationBasedOnDocuments', color: 'success', icon: <VerifiedIcon sx={{ fontSize: 14 }} /> },
  caution: { labelKey: 'chat.verificationPartlyUnsupported', color: 'warning', icon: <InfoIcon sx={{ fontSize: 14 }} /> },
  warning: { labelKey: 'chat.verificationNeedsReviewLabel', color: 'error', icon: <WarningIcon sx={{ fontSize: 14 }} /> },
};

// Icon prefix for flag severity (WCAG: don't rely on color alone)
const FLAG_ICON: Record<string, string> = {
  warning: '⚠ ',
  caution: 'ℹ ',
};

const VerificationBadge: React.FC<{ summary: VerificationSummary; onToggle?: (open: boolean) => void }> = ({ summary, onToggle }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const config = VERIFICATION_CONFIG[summary.level] || VERIFICATION_CONFIG.ok;

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    onToggle?.(next);
  };

  // Filter out 'ok' severity flags for display (ok flags are informational/logging only)
  const displayFlags = summary.flags.filter((f) => f.severity !== 'ok');

  return (
    <Box sx={{ mt: 1.5 }}>
      <Chip
        icon={config.icon as React.ReactElement}
        label={t(config.labelKey)}
        color={config.color}
        variant="filled"
        size="small"
        onClick={handleToggle}
        onDelete={handleToggle}
        deleteIcon={
          <ExpandMoreIcon
            sx={{
              fontSize: 16,
              color: 'inherit',
              transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        }
        sx={{
          height: 26,
          fontSize: 11,
          fontWeight: 600,
          cursor: 'pointer',
          color: '#fff',
          '& .MuiChip-icon': { fontSize: 14, color: 'inherit' },
          '& .MuiChip-deleteIcon': { color: 'inherit' },
        }}
      />
      <Collapse in={open}>
        <Box
          sx={{
            mt: 1,
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'rgba(0, 0, 0, 0.02)',
            border: '1px solid',
            borderColor: 'divider',
            fontSize: 12,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.25 }}>
            {t('chat.verificationDetailsTitle')}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            {t('chat.verificationDetailsDescription')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: displayFlags.length > 0 ? 1 : 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '2px', bgcolor: 'rgba(46, 125, 50, 0.35)', flexShrink: 0 }} />
              {t('chat.verificationSupportedClaims', { count: summary.supported_claims })}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={{ display: 'inline-block', width: 10, height: 10, borderRadius: '2px', bgcolor: 'rgba(237, 108, 2, 0.4)', flexShrink: 0 }} />
              {t('chat.verificationUnsupportedClaims', { count: summary.uncited_claims })}
            </Typography>
            {summary.weak_citations > 0 && (
              <Typography variant="caption" color="text.secondary">
                {t('chat.verificationWeakCitations', { count: summary.weak_citations })}
              </Typography>
            )}
          </Box>
          {displayFlags.length > 0 && (
            <Box component="ul" sx={{ m: 0, pl: 2 }}>
              {displayFlags.map((flag, i) => (
                <Typography
                  key={i}
                  component="li"
                  variant="caption"
                  color="text.primary"
                  sx={{ lineHeight: 1.6 }}
                >
                  {FLAG_ICON[flag.severity] || ''}{flag.message}
                </Typography>
              ))}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};

// ------------------------------------------------------------------
// Highlighted content renderer (Gemini-style sentence highlighting)
// ------------------------------------------------------------------

const HIGHLIGHT_COLORS: Record<string, string> = {
  supported: 'rgba(46, 125, 50, 0.15)',    // green
  unsupported: 'rgba(237, 108, 2, 0.18)',  // orange
};

/** Strip markdown formatting and collapse whitespace for fuzzy matching */
const normalize = (s: string) =>
  s.replace(/\*\*/g, '').replace(/\s+/g, ' ').trim();

/**
 * Find the range in `haystack` that best matches `needle` (normalized).
 * Returns [start, end] in the original haystack, or null.
 */
const fuzzyFind = (haystack: string, needle: string, startFrom: number = 0): [number, number] | null => {
  const normNeedle = normalize(needle);
  if (!normNeedle) return null;

  // Slide through haystack, building normalized windows
  let normIdx = 0;
  let matchStart = -1;

  for (let i = startFrom; i < haystack.length; i++) {
    const ch = haystack[i];
    // Skip markdown ** and collapse whitespace
    if (ch === '*' && i + 1 < haystack.length && haystack[i + 1] === '*') {
      i++; // skip **
      continue;
    }
    const normCh = /\s/.test(ch) ? ' ' : ch;
    // Skip consecutive spaces in normalized comparison
    if (normCh === ' ' && normIdx > 0 && normNeedle[normIdx - 1] === ' ') {
      continue;
    }

    if (normCh === normNeedle[normIdx]) {
      if (normIdx === 0) matchStart = i;
      normIdx++;
      if (normIdx === normNeedle.length) {
        return [matchStart, i + 1];
      }
    } else {
      // Reset and retry from matchStart + 1
      if (matchStart >= 0) {
        i = matchStart;
        matchStart = -1;
        normIdx = 0;
      }
    }
  }
  return null;
};

const HighlightedContent: React.FC<{
  content: string;
  annotations: SentenceAnnotation[];
  variant?: 'body1' | 'body2';
}> = ({ content, annotations, variant = 'body2' }) => {
  const { t } = useTranslation();

  if (!annotations || annotations.length === 0) {
    return isMarkdownContent(content)
      ? <MarkdownRenderer content={content} variant={variant} />
      : <Typography variant={variant}>{content}</Typography>;
  }

  // Find character ranges for each annotation in the original content
  const ranges: Array<{ start: number; end: number; status: string }> = [];
  let searchFrom = 0;
  for (const ann of annotations) {
    if (ann.status === 'neutral') continue;
    const range = fuzzyFind(content, ann.text, searchFrom);
    if (range) {
      ranges.push({ start: range[0], end: range[1], status: ann.status });
      searchFrom = range[1];
    }
  }

  // Build highlighted spans from ranges
  const parts: React.ReactNode[] = [];
  let pos = 0;
  let key = 0;
  for (const r of ranges) {
    if (r.start > pos) {
      parts.push(<span key={key++}>{content.slice(pos, r.start)}</span>);
    }
    const bg = HIGHLIGHT_COLORS[r.status];
    parts.push(
      <span
        key={key++}
        style={{
          backgroundColor: bg,
          borderRadius: 4,
          padding: '2px 3px',
          transition: 'background-color 0.3s',
        }}
        title={r.status === 'supported'
          ? t('chat.verificationSupportedTitle')
          : t('chat.verificationUnsupportedTitle')}
      >
        {content.slice(r.start, r.end)}
      </span>,
    );
    pos = r.end;
  }
  if (pos < content.length) {
    parts.push(<span key={key++}>{content.slice(pos)}</span>);
  }

  return (
    <Typography variant={variant} component="div" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.8 }}>
      {parts}
    </Typography>
  );
};

// Progressive thinking messages — resolved via i18n at render time
const THINKING_KEYS = ['chat.thinking', 'chat.stillThinking', 'chat.almostThere'];

const NotionStyleMessageList: React.FC<NotionStyleMessageListProps> = ({
  messages,
  isLoading,
  onSearchResultClick,
  onAddToContext,
  selectedFileIds,
  onRetryWithMode,
}) => {
  const { t } = useTranslation();
  // Track which messages have active verification highlighting
  const [highlightedMsgIds, setHighlightedMsgIds] = useState<Set<string>>(new Set());

  // Track which phase of thinking message to show (0, 1, or 2)
  const [thinkingPhase, setThinkingPhase] = useState(0);

  // Update thinking phase based on elapsed time
  useEffect(() => {
    if (!isLoading) {
      setThinkingPhase(0);
      return;
    }

    // Reset phase immediately when loading starts to handle rapid toggle
    setThinkingPhase(0);

    const timers = [
      setTimeout(() => setThinkingPhase(1), 4000),  // 4秒後
      setTimeout(() => setThinkingPhase(2), 10000), // 10秒後
    ];

    return () => timers.forEach(clearTimeout);
  }, [isLoading]);

  const handleCopyMessage = (content: string) => {
    navigator.clipboard.writeText(content);
  };

  const renderMessage = (message: ExtendedChatMessage, msgIndex: number) => {
    const isUser = message.role === 'user';
    const isSystem = message.role === 'system' || message.content.startsWith('✅');

    // System message style
    if (isSystem) {
      return (
        <Box
          key={message.id}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 2,
              backgroundColor: 'rgba(76, 175, 80, 0.08)',
              border: '1px solid rgba(76, 175, 80, 0.2)',
            }}
          >
            {message.content.startsWith('✅') ? (
              <CheckCircleIcon sx={{ fontSize: 18, color: '#4CAF50' }} />
            ) : (
              <InfoIcon sx={{ fontSize: 18, color: '#2196F3' }} />
            )}
            <Typography
              variant="body2"
              sx={{
                fontSize: 13,
                color: 'rgba(50, 48, 44, 0.8)',
                fontWeight: 400,
              }}
            >
              {message.content.replace('✅ ', '')}
            </Typography>
          </Box>
        </Box>
      );
    }

    if (isUser) {
      // User message style - right aligned with background
      return (
        <Box
          key={message.id}
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            mb: 2,
            position: 'relative',
            '&:hover .copy-button': {
              opacity: 1,
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              maxWidth: '90%',
            }}
          >
            {/* Action buttons - appear on hover */}
            <Box className="copy-button" sx={{ display: 'flex', flexDirection: 'column', opacity: 0, transition: 'opacity 0.1s' }}>
              <IconButton
                size="small"
                onClick={() => handleCopyMessage(message.content)}
                sx={{
                  width: 24,
                  height: 24,
                  mt: 0.75,
                  color: 'rgba(71, 70, 68, 0.6)',
                  '&:hover': { backgroundColor: 'rgba(55, 53, 47, 0.08)' },
                }}
              >
                <CopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>

            {/* Message bubble */}
            <Box
              sx={{
                px: 1.75,
                py: 0.75,
                borderRadius: 2,
                backgroundColor: 'rgba(84, 72, 49, 0.08)',
                maxWidth: '100%',
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: 14,
                  lineHeight: 1.5,
                  color: 'rgb(50, 48, 44)',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {message.content}
              </Typography>
            </Box>
          </Box>
        </Box>
      );
    } else {
      // Assistant message style - left aligned with AIONA icon
      return (
        <Box
          key={message.id}
          sx={{
            display: 'flex',
            mb: 3,
            position: 'relative',
            '&:hover .copy-button': {
              opacity: 1,
            },
          }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, width: '100%' }}>
            {/* AIONA Avatar */}
            <Avatar
              src={AionaIcon}
              sx={{
                width: 24,
                height: 24,
                backgroundColor: 'transparent',
                flexShrink: 0,
                mt: 0.25,
              }}
            />

            {/* Message content (with optional verification highlighting) */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {highlightedMsgIds.has(message.id) && message.verificationSummary?.sentence_annotations?.length ? (
                <Box sx={{ fontSize: 14, lineHeight: 1.8, color: 'rgb(50, 48, 44)' }}>
                  <HighlightedContent
                    content={message.content}
                    annotations={message.verificationSummary.sentence_annotations}
                    variant="body2"
                  />
                </Box>
              ) : isMarkdownContent(message.content) ? (
                <MarkdownRenderer
                  content={message.content}
                  variant="body2"
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.7,
                    color: 'rgb(50, 48, 44)',
                    '& p': {
                      margin: '0.5rem 0',
                      '&:first-of-type': {
                        marginTop: 0,
                      },
                      '&:last-child': {
                        marginBottom: 0,
                      },
                    },
                  }}
                />
              ) : (
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    fontSize: 14,
                    lineHeight: 1.7,
                    color: 'rgb(50, 48, 44)',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {message.content}
                </Typography>
              )}

              {/* Display search results if available */}
              {message.searchResults && message.searchResults.results && message.searchResults.results.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <SearchResultsCard
                    query={message.searchResults.query}
                    results={message.searchResults.results}
                    onResultClick={onSearchResultClick}
                    onAddToContext={onAddToContext}
                    selectedFileIds={selectedFileIds}
                  />
                </Box>
              )}

              {/* Grounding warning — shown at message level since SearchResultsCard may not render */}
              {message.groundingStatus === 'general_fallback' && (
                <Alert
                  severity="warning"
                  sx={{
                    mt: 1.5,
                    py: 0,
                    fontSize: 12,
                    '& .MuiAlert-message': { fontSize: 12 },
                  }}
                >
                  {t('chat.generalFallbackWarning')}
                </Alert>
              )}

              {message.webSearchStatus?.status === 'disabled' && (
                <Alert
                  severity="info"
                  sx={{
                    mt: 1.5,
                    py: 0,
                    fontSize: 12,
                    '& .MuiAlert-message': { fontSize: 12 },
                  }}
                >
                  Web検索は現在未対応または無効のため実行されていません。社内文書のみで回答しています。
                </Alert>
              )}

              {/* Verification badge + collapsible details (Sprint 10) */}
              {message.verificationSummary && (
                <VerificationBadge
                  summary={message.verificationSummary}
                  onToggle={(open) => {
                    setHighlightedMsgIds((prev) => {
                      const next = new Set(prev);
                      if (open) next.add(message.id);
                      else next.delete(message.id);
                      return next;
                    });
                  }}
                />
              )}

              {/* Inline warning for verification issues (warning level only) */}
              {message.verificationSummary?.level === 'warning' && message.groundingStatus !== 'general_fallback' && (
                <Alert
                  severity="warning"
                  sx={{
                    mt: 1,
                    py: 0,
                    fontSize: 12,
                    '& .MuiAlert-message': { fontSize: 12 },
                  }}
                >
                  {t('chat.verificationNeedsReview')}
                </Alert>
              )}

              {/* Retry link inside message box — always visible */}
              {onRetryWithMode && (() => {
                for (let i = msgIndex - 1; i >= 0; i--) {
                  if (messages[i].role === 'user') {
                    const userContent = messages[i].content;
                    return (
                      <Typography
                        variant="caption"
                        onClick={() => onRetryWithMode(userContent)}
                        sx={{
                          color: 'primary.main',
                          cursor: 'pointer',
                          mt: 1,
                          display: 'block',
                          textAlign: 'right',
                          '&:hover': { textDecoration: 'underline' },
                          fontSize: '0.7rem',
                        }}
                      >
                        {t('chat.retryWithMode')}
                      </Typography>
                    );
                  }
                }
                return null;
              })()}
            </Box>

            {/* Copy button - appears on hover */}
            <IconButton
              className="copy-button"
              size="small"
              onClick={() => handleCopyMessage(message.content)}
              sx={{
                opacity: 0,
                transition: 'opacity 0.1s',
                width: 24,
                height: 24,
                mt: 0.25,
                color: 'rgba(71, 70, 68, 0.6)',
                '&:hover': {
                  backgroundColor: 'rgba(55, 53, 47, 0.08)',
                },
              }}
            >
              <CopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
        </Box>
      );
    }
  };

  return (
    <Box>
      {messages.map(renderMessage)}

      {isLoading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Avatar
            src={AionaIcon}
            sx={{
              width: 24,
              height: 24,
              backgroundColor: 'transparent',
            }}
          />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: 14 }}>
              {t(THINKING_KEYS[thinkingPhase])}
            </Typography>
          </Box>
        </Box>
      )}

      {messages.length === 0 && !isLoading && (
        <Box sx={{ textAlign: 'center', mt: 4, px: 2 }}>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(55, 53, 47, 0.65)',
              fontSize: 14,
              lineHeight: 1.6,
            }}
          >
            {t('chat.notionEmptyDescription')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default NotionStyleMessageList;
